import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Autocomplete,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  InputAdornment,
  IconButton,
  Chip,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  CircularProgress,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Add as AddIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
// Use these imports for production with Firebase
// import { collection, getDocs, query, where, orderBy } from 'firebase-shim';
// import { db } from '../../../firebase';

// Use these imports for development without Firebase
// import { collection, getDocs, query, where, orderBy } from '../../../firebase/firebase-mock';
// import { db } from '../../../firebase/config';

import indexedDBService from '../../../utils/indexedDBService';
import NewClientDialog from '../../dialogs/NewClientDialog';

// Import interfaces from CreateInvoiceDialog
interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  client: string;
  clientId: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
    state: string;
  };
  items: any[];
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxable: boolean;
  userId: string;
  createdAt: string;
}

interface ClientSelectionStepProps {
  invoiceData: InvoiceData;
  updateInvoiceData: (data: Partial<InvoiceData>) => void;
  userId?: string;
  clients?: any[];
}

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  gstin?: string;
  userId: string;
  createdAt: any;
  state: string;
}

// Add a more flexible data type for client data from various sources
interface ClientData {
  [key: string]: any; // Allow any property to exist on the data object
}

const ClientSelectionStep: React.FC<ClientSelectionStepProps> = ({
  invoiceData,
  updateInvoiceData,
  userId = '',
  clients: initialClients = [],
}) => {
  // State for clients
  const [clients, setClients] = useState<Client[]>(initialClients);
  const [filteredClients, setFilteredClients] = useState<Client[]>(initialClients);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(initialClients.length === 0);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // State for new client dialog
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  // Fetch clients when component mounts
  useEffect(() => {
    if (initialClients && initialClients.length > 0) {
      setClients(initialClients);
      setFilteredClients(initialClients);
      setLoading(false);

      // If invoice already has a client, find and select it
      if (invoiceData.clientId) {
        const existingClient = initialClients.find(c => c.id === invoiceData.clientId);
        if (existingClient) {
          setSelectedClient(existingClient);
        }
      }
      return;
    }

    const fetchClients = async () => {
      try {
        const allClients = await indexedDBService.getAllItems<any>('clients');

        // Filter by userId
        const userClients = userId ? allClients.filter(c => c.userId === userId) : allClients;

        // Transform the mock data to match our Client interface
        const clientsList = userClients.map(data => {

          // Extract name field with fallbacks
          const name = data.name || data.clientName || data.customer_name || 'Unnamed Client';

          // Extract address field with fallbacks
          const address = data.address || data.clientAddress || '';

          // Extract gstin field with fallbacks
          const gstin = data.gstin || data.GSTIN || data.gst || '';

          // Extract email field with fallbacks
          const email = data.email || data.clientEmail || '';

          // Extract phone field with fallbacks
          const phone = data.phone || data.phoneNumber || data.mobile || '';

          // Extract state field with fallbacks
          const state = data.state || data.clientState || data.place_of_supply || '';

          return {
            id: data.id,
            name,
            email,
            phone,
            address,
            gstin,
            userId: data.userId || userId,
            createdAt: data.createdAt || new Date().toISOString(),
            state
          } as Client;
        });

        // Sort by name
        clientsList.sort((a, b) => a.name.localeCompare(b.name));

        setClients(clientsList);
        setFilteredClients(clientsList);
        setLoading(false);

        // If invoice already has a client, find and select it
        if (invoiceData.clientId) {
          const existingClient = clientsList.find(c => c.id === invoiceData.clientId);
          if (existingClient) {
            setSelectedClient(existingClient);
          }
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setLoading(false);
      }
    };

    fetchClients();
  }, [userId, invoiceData.clientId, initialClients]);

  // Filter clients based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredClients(clients);
      return;
    }

    const filtered = clients.filter(client =>
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (client.email && client.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (client.phone && client.phone.includes(searchQuery)) ||
      (client.gstin && client.gstin.includes(searchQuery))
    );

    setFilteredClients(filtered);
  }, [searchQuery, clients]);

  // Update invoice data when client is selected
  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);

    updateInvoiceData({
      client: client.name,
      clientId: client.id,
      clientDetails: {
        address: client.address || '',
        gstin: client.gstin || '',
        email: client.email || '',
        phone: client.phone || '',
        state: client.state || ''
      }
    });
  };

  // Open dialog to add a new client
  const handleAddClient = () => {
    setEditingClient(null);
    setNewClientDialogOpen(true);
  };

  // Open dialog to edit an existing client
  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setNewClientDialogOpen(true);
  };

  // Handle client creation or update
  const handleClientSaved = (savedClient: any) => {
    // Cast the saved client to our Client type, ensuring state field exists
    const client: Client = {
      id: savedClient.id,
      name: savedClient.name || '',
      email: savedClient.email || '',
      phone: savedClient.phone || '',
      address: savedClient.address || '',
      gstin: savedClient.gstin || '',
      userId: savedClient.userId || userId,
      createdAt: savedClient.createdAt || new Date().toISOString(),
      state: savedClient.state || '' // Ensure state is set even if not provided by NewClientDialog
    };

    // If it's a new client
    if (!clients.some(c => c.id === client.id)) {
      setClients(prev => [...prev, client]);
    } else {
      // If it's an existing client being updated
      setClients(prev =>
        prev.map(c => (c.id === client.id ? client : c))
      );
    }

    // Select the client for the invoice
    handleSelectClient(client);
    setNewClientDialogOpen(false);
  };

  // Get initial letter for avatar
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Client Information
      </Typography>

      {/* Search and add client */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs>
          <TextField
            fullWidth
            placeholder="Search clients"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleAddClient}
          >
            New Client
          </Button>
        </Grid>
      </Grid>

      {/* Selected client card */}
      {selectedClient && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            borderColor: 'primary.main',
            borderWidth: 2,
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'primary.main',
                }}
              >
                {getInitials(selectedClient.name)}
              </Avatar>
            </Grid>

            <Grid item xs>
              <Typography variant="h6">{selectedClient.name}</Typography>

              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                {selectedClient.email && (
                  <Grid item>
                    <Chip
                      size="small"
                      icon={<EmailIcon fontSize="small" />}
                      label={selectedClient.email}
                      variant="outlined"
                    />
                  </Grid>
                )}

                {selectedClient.phone && (
                  <Grid item>
                    <Chip
                      size="small"
                      icon={<PhoneIcon fontSize="small" />}
                      label={selectedClient.phone}
                      variant="outlined"
                    />
                  </Grid>
                )}

                {selectedClient.gstin && (
                  <Grid item>
                    <Chip
                      size="small"
                      icon={<BusinessIcon fontSize="small" />}
                      label={`GSTIN: ${selectedClient.gstin}`}
                      variant="outlined"
                    />
                  </Grid>
                )}
              </Grid>

              {selectedClient.address && (
                <Box sx={{ display: 'flex', alignItems: 'start', mt: 1 }}>
                  <PlaceIcon fontSize="small" sx={{ mr: 0.5, mt: 0.25, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {selectedClient.address}
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid item>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => handleEditClient(selectedClient)}
              >
                Edit
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Clients list */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 4 }}>
        Select a client
      </Typography>

      <Paper variant="outlined" sx={{ height: 350, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredClients.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No clients found
            </Typography>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={handleAddClient}
            >
              Add New Client
            </Button>
          </Box>
        ) : (
          <List disablePadding>
            {filteredClients.map((client, index) => (
              <React.Fragment key={client.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  button
                  selected={selectedClient?.id === client.id}
                  onClick={() => handleSelectClient(client)}
                >
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {getInitials(client.name)}
                  </Avatar>
                  <ListItemText
                    primary={client.name}
                    secondary={
                      <React.Fragment>
                        {client.email && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {client.email}
                          </Typography>
                        )}
                        {client.phone && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                          >
                            {client.phone}
                          </Typography>
                        )}
                      </React.Fragment>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClient(client);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* New/Edit Client Dialog */}
      <NewClientDialog
        open={newClientDialogOpen}
        onClose={() => setNewClientDialogOpen(false)}
        onSave={handleClientSaved}
        client={editingClient}
        userId={userId}
      />
    </Box>
  );
};

export default ClientSelectionStep; 
